import React, { useEffect, useState } from 'react';

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

/**
 * Posición del toast en pantalla. Ambas posiciones quedan a la misma altura que
 * el launcher de Intercom (≈abajo-del-todo) y desplazadas lateralmente para
 * quedar JUNTO al círculo, no encima de él.
 *  - `bottom-right` (por defecto): el launcher de Intercom vive abajo-derecha,
 *    así que el toast queda a su IZQUIERDA, a la misma altura.
 *  - `bottom-left`: cuando un panel lateral derecho está abierto, Intercom se
 *    mueve a abajo-izquierda; el toast queda a su DERECHA, a la misma altura.
 */
export type ToastPosition = 'bottom-right' | 'bottom-left';

export interface ToastProps {
  message: string;
  variant?: ToastVariant;
  duration?: number;
  position?: ToastPosition;
  onDismiss: () => void;
}

// Coordenadas por posición. El toast queda a la misma altura que el launcher de
// Intercom (≈24px desde abajo) y desplazado ≈96px lateralmente para dejar libre
// el círculo (≈60px + padding ≈20px) y quedar JUNTO a él, no encima.
//  - `bottom-right`: Intercom vive abajo-derecha → toast a su izquierda.
//  - `bottom-left`: con SplitPanel abierto Intercom se va abajo-izquierda → toast a su derecha.
const positionStyles: Record<ToastPosition, { bottom: number; left?: number; right?: number }> = {
  'bottom-right': { bottom: 24, right: 96 },
  'bottom-left': { bottom: 24, left: 96 },
};

const variantStyles: Record<ToastVariant, { bg: string; color: string; icon: React.ReactNode }> = {
  success: {
    bg: '#DCFCE7',
    color: '#166534',
    icon: (
      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
      </svg>
    ),
  },
  error: {
    bg: '#FEE2E2',
    color: '#991B1B',
    icon: (
      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
      </svg>
    ),
  },
  info: {
    bg: '#EEF2FF',
    color: '#1E40AF',
    icon: (
      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20A10 10 0 0012 2z" />
      </svg>
    ),
  },
  warning: {
    bg: '#FEF3C7',
    color: '#92400E',
    icon: (
      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      </svg>
    ),
  },
};

export const Toast = ({ message, variant = 'success', duration = 3000, position = 'bottom-right', onDismiss }: ToastProps) => {
  const [visible, setVisible] = useState(true);
  const styles = variantStyles[variant];
  const pos = positionStyles[position];

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(onDismiss, 300);
    }, duration);
    return () => clearTimeout(timer);
  }, [duration, onDismiss]);

  return (
    <div
      style={{
        position: 'fixed',
        ...pos,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 16px',
        borderRadius: 12,
        background: styles.bg,
        color: styles.color,
        fontFamily: 'Nunito Sans, sans-serif',
        fontWeight: 700,
        fontSize: 13,
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(8px)',
        maxWidth: 340,
        pointerEvents: 'none',
      }}
    >
      <span style={{ flexShrink: 0 }}>{styles.icon}</span>
      <span>{message}</span>
    </div>
  );
};

/* ─────────────────────────────────────────────
   useToast — hook para mostrar toasts fácilmente
   ───────────────────────────────────────────── */

export interface ToastState {
  message: string;
  variant: ToastVariant;
  key: number;
  /** Milisegundos antes de auto-cerrarse. `undefined` deja el default de `Toast` (3000). */
  duration?: number;
}

/**
 * @param defaultPosition posición usada por todos los toasts de este hook.
 *   Por defecto `bottom-right`; pásala como `bottom-left` cuando el toast se
 *   renderiza dentro de un panel lateral derecho que taparía la esquina derecha.
 */
export const useToast = (defaultPosition: ToastPosition = 'bottom-right') => {
  const [toast, setToast] = useState<ToastState | null>(null);

  /**
   * @param duration milisegundos antes de auto-cerrarse. Omitido deja el default de `Toast`
   *   (3000), que es el adecuado para una confirmación rutinaria. Pásalo más alto cuando el
   *   mensaje confirma una acción irreversible o de consecuencias visibles, donde 3 s no dan
   *   para leerlo.
   */
  const show = (message: string, variant: ToastVariant = 'success', duration?: number) => {
    setToast({ message, variant, key: Date.now(), duration });
  };

  const dismiss = () => setToast(null);

  const toastElement = toast ? (
    <Toast key={toast.key} message={toast.message} variant={toast.variant} duration={toast.duration} position={defaultPosition} onDismiss={dismiss} />
  ) : null;

  return { show, toastElement };
};
